/// <reference types="vite/client" />
import React, { useState } from 'react';
import IntroStep from './IntroStep';
import RulesStep from './RulesStep';
import ProfileSetupStep from './ProfileSetupStep';
import ConnectStravaStep from './ConnectStravaStep';
import { User } from '../../types';
import { completeOnboarding } from '../../services/firebase';
import { getStravaAuthUrl } from '../../services/strava';
import { useNavigate } from 'react-router-dom';

interface OnboardingFlowProps {
  user: User;
}

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ user }) => {
  // If user is already connected to Strava (e.g. returning from OAuth), jump to connect step to show success
  const [step, setStep] = useState<'intro' | 'rules' | 'profile' | 'connect'>(
    user.isConnectedStrava ? 'connect' : 'intro'
  );
  const navigate = useNavigate();
  const [isConnecting, setIsConnecting] = useState(false);

  const handleComplete = async () => {
    try {
      await completeOnboarding(user.id);
      navigate('/');
    } catch (error) {
      console.error("Failed to complete onboarding", error);
    }
  };

  const handleConnectStrava = async () => {
    setIsConnecting(true);
    // Mark onboarding as complete immediately to avoid redirect loops when returning
    try {
      await completeOnboarding(user.id);
    } catch (e) {
      console.error("Error completing onboarding", e);
    }
    // Redirect to Strava OAuth
    window.location.href = getStravaAuthUrl();
  };

  // If user is already connected (e.g. from previous session or just came back from OAuth), allow finishing
  const isConnected = user.isConnectedStrava;

  const renderStep = () => {
    switch (step) {
      case 'intro':
        return <IntroStep userName={user.name} onNext={() => setStep('rules')} />;
      case 'rules':
        return <RulesStep onNext={() => setStep('profile')} />;
      case 'profile':
        return <ProfileSetupStep user={user} onNext={() => setStep('connect')} />;
      case 'connect':
        // ConnectStravaStep now handles both connected and not-connected states internally
        return (
          <ConnectStravaStep
            onConnect={handleConnectStrava}
            onComplete={handleComplete}
            isConnecting={isConnecting}
            isAlreadyConnected={isConnected}
          />
        );
    }
  };

  return (
    <div className="w-full min-h-[100dvh] flex flex-col items-center justify-center font-sans bg-slate-50 md:bg-slate-100 md:p-6 transition-colors duration-500">

      {/* Card Container - Full width/height on Mobile, Centered Card on Desktop */}
      <div className="w-full h-[100dvh] md:h-[650px] md:max-w-5xl bg-slate-50 md:bg-white md:rounded-3xl md:shadow-2xl overflow-hidden flex flex-col relative transition-all duration-500">

        {/* Header / Progress - Fixed at top */}
        <div className="w-full max-w-lg md:max-w-3xl mx-auto pt-6 px-4 flex-none z-20">
          {/* Progress Dots */}
          <div className="flex justify-center gap-3 mb-2 md:mb-6">
            <div className={`h-1.5 rounded-full transition-all duration-500 ease-out ${step === 'intro' ? 'w-12 bg-indigo-600 shadow-lg shadow-indigo-500/30' : 'w-2 bg-slate-300'}`} />
            <div className={`h-1.5 rounded-full transition-all duration-500 ease-out ${step === 'rules' ? 'w-12 bg-indigo-600 shadow-lg shadow-indigo-500/30' : 'w-2 bg-slate-300'}`} />
            <div className={`h-1.5 rounded-full transition-all duration-500 ease-out ${step === 'profile' ? 'w-12 bg-indigo-600 shadow-lg shadow-indigo-500/30' : 'w-2 bg-slate-300'}`} />
            <div className={`h-1.5 rounded-full transition-all duration-500 ease-out ${step === 'connect' ? 'w-12 bg-indigo-600 shadow-lg shadow-indigo-500/30' : 'w-2 bg-slate-300'}`} />
          </div>
        </div>

        {/* Main Content Area - Flexible height */}
        <div className="w-full max-w-lg md:max-w-full mx-auto flex-1 relative overflow-hidden flex flex-col pt-6 md:pt-0">
          {/* Step Content Container */}
          <div key={step} className="animate-slide-in-right h-full flex flex-col">
            {renderStep()}
          </div>
        </div>

        {step === 'connect' && !isConnected && (
          <div className="w-full max-w-lg mx-auto px-4 pb-6 pt-2 flex-none text-center md:absolute md:bottom-4 md:left-0 md:right-0 md:z-20">
            <button
              onClick={handleComplete}
              className="text-sm text-slate-500 hover:text-indigo-600 font-medium transition-colors hover:underline decoration-indigo-200 underline-offset-4"
            >
              Bỏ qua, tôi sẽ kết nối sau
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
